import FlexCenter from 'App/components/styles/FlexCenter';
import TherigyInfoMessage from 'App/components/TherigyInfoMessage/TherigyInfoMessage';
import { disablePatient, update } from 'App/services/PatientService';
import { orderBy, without } from 'lodash';
import React, { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';

const PdxPatientResolveDuplicateModal = ({ patients, onClose }) => {
    const [selectedRow, setSelectedRow] = useState();

    const handleResolvePatient = async () => {
        if (!selectedRow?.ID) return;

        try {
            await update(selectedRow.ID, {
                external_id: selectedRow.PDXExternalId,
            });

            const others = without(patients, selectedRow);
            await Promise.all(others.map((row) => disablePatient(row.ID)));

            onClose(selectedRow);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error resolving patient:', error);
        }
    };

    return (
        <Modal show size="lg">
            <Modal.Header>
                <Modal.Title role="heading">Sync Duplicate Patient Records</Modal.Title>
            </Modal.Header>

            <Modal.Body id="pdx-patient-duplicate-resolve-body">
                <TherigyInfoMessage message="While attempting to sync, we identified potential duplicate patient records. Please select the patient record to sync with rx.com. Patient records that are not synced will be disabled in TherigySTM." />

                <div className="table-responsive">
                    <table className="therigy-table">
                        <thead>
                            <tr>
                                <td>Last Name</td>
                                <td>First Name</td>
                                <td>Address</td>
                                <td>Birth Date</td>
                                <td>Number of Medications</td>
                                <td>Number of Activities</td>
                                <td>Sync</td>
                            </tr>
                        </thead>
                        <tbody>
                            {orderBy(patients ?? [], ['activityCount'], ['desc']).map((row) => (
                                <tr
                                    className={`${
                                        selectedRow?.ID && selectedRow?.ID !== row.ID ? 'disabled' : 'selected'
                                    }`}
                                    key={row.ID}
                                >
                                    <td>{row.LastName}</td>
                                    <td>{row.FirstName}</td>
                                    <td>
                                        {row.AddressLine1} {row.AddressLine2}
                                    </td>
                                    <td>{row.BirthDate}</td>
                                    <td>{row.medicationCount}</td>
                                    <td>{row.activityCount}</td>
                                    <td // eslint-disable-line jsx-a11y/control-has-associated-label
                                        // TODO: Fix this the next time the file is edited.
                                        className="action"
                                    >
                                        <div className="form-check">
                                            <label // eslint-disable-line jsx-a11y/label-has-associated-control
                                                // TODO: Fix this the next time the file is edited.
                                                className="form-check-label"
                                                htmlFor={`select-${row.ID}`}
                                            >
                                                <input
                                                    id={`select-${row.ID}`}
                                                    className="form-check-input"
                                                    type="radio"
                                                    onClick={() => setSelectedRow(row)}
                                                />
                                            </label>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <FlexCenter>
                    {selectedRow?.ID && (
                        <TherigyInfoMessage
                            class="footer-message"
                            message="The records not selected will be automatically disabled."
                        />
                    )}
                    <div style={{ flexGrow: 1 }} />
                    <Button variant="outline-secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button disabled={!selectedRow?.ID} variant="primary" onClick={handleResolvePatient}>
                        Sync
                    </Button>
                </FlexCenter>
            </Modal.Footer>
        </Modal>
    );
};

PdxPatientResolveDuplicateModal.propTypes = {
    patients: PropTypes.arrayOf(
        PropTypes.shape({
            ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            PDXExternalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            LastName: PropTypes.string,
            FirstName: PropTypes.string,
            AddressLine1: PropTypes.string,
            AddressLine2: PropTypes.string,
            BirthDate: PropTypes.string,
            medicationCount: PropTypes.number,
            activityCount: PropTypes.number,
        })
    ),
    onClose: PropTypes.func.isRequired,
};

PdxPatientResolveDuplicateModal.defaultProps = {
    patients: [],
};

export default PdxPatientResolveDuplicateModal;
